import {
  MAX_WAY_PACKAGE_ID_LENGTH,
  PackageMappingFormRow,
  PackageMappingRow,
} from '../models/package-mapping.model';
import { OrderUtils } from '../../orders/utils/order.utils';

export function mapPackageMappingFromApi(row: PackageMappingRow): PackageMappingFormRow {
  const wayId = typeof row.way_package_id === 'string' ? row.way_package_id.trim() : '';
  const washTypes = Array.isArray(row.wash_types)
    ? row.wash_types.filter((value): value is string => typeof value === 'string')
    : [];
  return {
    package_location_id: row.package_location_id,
    package_id: row.package_id,
    package_name: row.package_name,
    type: row.type ?? null,
    wash_types: washTypes,
    way_package_id: wayId,
    saved_way_package_id: wayId,
    fieldError: '',
  };
}

/** Labels for wash offer types (single wash / monthly membership). */
export function getPackageMappingWashTypeLabels(row: PackageMappingFormRow): string[] {
  if (!row.wash_types?.length) {
    return [];
  }
  return row.wash_types
    .filter(value => typeof value === 'string' && value.length > 0)
    .map(value => OrderUtils.formatWashType(value))
    .filter(label => label.length > 0);
}

export function isPackageMappingDirty(row: PackageMappingFormRow): boolean {
  return row.way_package_id.trim() !== row.saved_way_package_id.trim();
}

export function getPackageMappingStatusLabel(row: PackageMappingFormRow): string {
  if (isPackageMappingDirty(row)) {
    return 'Pending save';
  }
  return row.way_package_id.trim() ? 'Mapped' : 'Unmapped';
}

export function getPackageMappingStatusClass(row: PackageMappingFormRow): string {
  if (isPackageMappingDirty(row)) {
    return 'pending';
  }
  return row.way_package_id.trim() ? 'active' : 'inactive';
}

export function arePackageMappingsDirty(rows: PackageMappingFormRow[]): boolean {
  return rows.some(row => isPackageMappingDirty(row));
}

/**
 * Validates mapping rows in place (sets fieldError). Returns true when valid.
 */
export function validatePackageMappings(rows: PackageMappingFormRow[]): {
  valid: boolean;
  error: string;
} {
  let valid = true;
  const seen = new Map<string, string>();

  for (const row of rows) {
    row.fieldError = '';
    const trimmed = row.way_package_id.trim();
    if (trimmed.length > MAX_WAY_PACKAGE_ID_LENGTH) {
      row.fieldError = `Must be ${MAX_WAY_PACKAGE_ID_LENGTH} characters or fewer`;
      valid = false;
      continue;
    }
    if (!trimmed) {
      continue;
    }
    const owner = seen.get(trimmed);
    if (owner) {
      row.fieldError = 'Duplicate Way package ID in this list';
      const other = rows.find(r => r.package_id === owner);
      if (other && !other.fieldError) {
        other.fieldError = 'Duplicate Way package ID in this list';
      }
      valid = false;
    } else {
      seen.set(trimmed, row.package_id);
    }
  }

  return {
    valid,
    error: valid ? '' : 'Fix validation errors before saving',
  };
}
